import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { useSite } from "../../context/SiteContext";
import { api, fileUrl, formatErr } from "../../lib/api";
import { Field, PageHeader, UploadButton, inputCls } from "../kit";
import { Collection } from "./Collections";
import { QuoteFormBuilder } from "./QuoteFormBuilder";

const PAGES = {
  Homepage: [["hero", "Hero"], ["page_background", "Page background (below hero)"], ["stats", "Statistics"], ["services_section", "Services heading"], ["how_it_works", "How it works"], ["videos_section", "Video tutorial heading"],
    ["reviews_section", "Reviews heading"], ["quote_section", "Quotation form heading"], ["__quote_form", "Quotation form questions"], ["faq_section", "FAQ heading"]],
  "Contact page": [["contact", "Contact information"]],
  Footer: [["footer", "Footer"]],
  Services: [["__services", "Services list"]],
};
const nice = (k) => k.replace(/_/g, " ").replace(/\bbg\b/, "background").replace(/\b\w/g, (c) => c.toUpperCase());
const isImg = (k) => /^bg_|image/.test(k);
const isLong = (k) => /description|text|tagline/.test(k);

function Value({ k, v, onChange }) {
  if (isImg(k))
    return (
      <div className="space-y-2">
        {v && <img src={fileUrl(v)} alt="" className="w-full max-w-sm h-32 object-cover rounded-lg border border-[#007bff]/30" />}
        <div className="flex gap-2"><Input value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder="Image URL" /><UploadButton scope="public" onUploaded={(r) => onChange(r.url)} testid={`content-upload-${k}`}>Upload</UploadButton></div>
      </div>
    );
  if (isLong(k)) return <Textarea rows={3} value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`content-field-${k}`} />;
  return <Input value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`content-field-${k}`} />;
}

function ListField({ k, items, onChange }) {
  const tmpl = Object.fromEntries(Object.keys(items[0] || { title: "", text: "" }).map((x) => [x, ""]));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-[#007bff]/25 p-3 grid sm:grid-cols-2 gap-3 relative">
          <span className="absolute -top-2.5 left-3 px-2 text-xs bg-[#050a18] text-[#00d9ff]">#{i + 1}</span>
          {Object.entries(it).map(([ik, iv]) => <Field key={ik} label={nice(ik)} full={isLong(ik)}><Value k={ik} v={iv} onChange={(nv) => onChange(items.map((x, j) => (j === i ? { ...x, [ik]: nv } : x)))} /></Field>)}
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-rose-300 text-xs flex items-center gap-1 sm:col-span-2"><Trash2 className="w-3 h-3" /> Remove</button>
        </div>
      ))}
      <button onClick={() => onChange([...items, tmpl])} className="btn-ghost !py-1.5 !px-3 text-sm" data-testid={`content-add-${k}`}><Plus className="w-4 h-4" /> Add item</button>
    </div>
  );
}

function SectionEditor({ k }) {
  const { site, reload } = useSite();
  const orig = site?.content?.[k] || {};
  const [f, setF] = useState(orig);
  useEffect(() => {
    setF(site?.content?.[k] || {});
  }, [k, site]);
  const save = async () => {
    try {
      await api.put(`/admin-content/${k}`, f);
      await reload();
      toast.success("Saved — live on the website");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="card-neon p-5 sm:p-6 space-y-4" data-testid={`content-editor-${k}`}>
      {Object.entries(f).map(([fk, fv]) => (
        <Field key={fk} label={nice(fk)}>
          {Array.isArray(fv) ? <ListField k={fk} items={fv} onChange={(nv) => setF({ ...f, [fk]: nv })} /> : typeof fv === "boolean" ? <Switch checked={fv} onCheckedChange={(nv) => setF({ ...f, [fk]: nv })} /> : <Value k={fk} v={fv ?? ""} onChange={(nv) => setF({ ...f, [fk]: nv })} />}
        </Field>
      ))}
      <div className="flex gap-2 pt-2"><button onClick={save} className="btn-electric !py-2" data-testid="content-save-btn">Save</button><button onClick={() => setF(orig)} className="btn-ghost !py-2" data-testid="content-cancel-btn">Cancel</button></div>
    </div>
  );
}

const SERVICE_FIELDS = [["title", "Title"], ["slug", "URL slug"], ["icon", "Icon (build, lifetime, rep, overall, badges, accounts)"], ["description", "Short description", "textarea"],
  ["details", "Full details (service page)", "textarea"], ["bullets", "Options / bullet points (one per line)", "list"], ["badge", "Badge (e.g. PS5 ONLY)"],
  ["current_label", "Quote form: current progress label"], ["target_label", "Quote form: target progress label"], ["image", "Image", "image"], ["order", "Display order", "number"], ["active", "Visible on website", "bool"]];

export default function ContentManager() {
  const [page, setPage] = useState("Homepage");
  const [sec, setSec] = useState("hero");
  const { reload } = useSite();
  return (
    <div data-testid="content-page">
      <PageHeader title="Website Content" desc="Select a page and section, edit, then Save. Changes go live immediately." />
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.keys(PAGES).map((p) => <button key={p} onClick={() => { setPage(p); setSec(PAGES[p][0][0]); }} className={`px-4 py-2 rounded-lg border font-cond font-bold uppercase tracking-wider ${page === p ? "bg-[#007bff] border-[#007bff]" : "border-[#007bff]/30 text-slate-300"}`} data-testid={`content-page-${p.toLowerCase().replace(/\s/g, "-")}`}>{p}</button>)}
      </div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-5">
        <div className="flex lg:flex-col gap-1 overflow-x-auto">
          {PAGES[page].map(([k, l]) => <button key={k} onClick={() => setSec(k)} className={`text-left px-3 py-2 rounded-lg whitespace-nowrap text-sm ${sec === k ? "bg-[#007bff]/20 text-white border border-[#007bff]/40" : "text-slate-400 border border-transparent hover:text-white"}`} data-testid={`content-section-${k}`}>{l}</button>)}
        </div>
        {sec === "__services" ? <Collection name="services" title="Services" fields={SERVICE_FIELDS} onChange={reload} embedded /> : sec === "__quote_form" ? <QuoteFormBuilder /> : <SectionEditor key={sec} k={sec} />}
      </div>
    </div>
  );
}
